// AI-CONTEXT: See docs/features/platform-revenue-churn-trend.md
import PageHeader from '../../components/layout/PageHeader'
import Table from '../../components/common/table/Table.jsx'
import TableCell from '../../components/common/table/TableCell.jsx'
import SectorTrendChart from '../../components/admin/SectorTrendChart'
import usePlatformChurn from '../../hooks/usePlatformChurn'
import buildSectorSeriesRows from '../../helpers/dashboard/buildSectorSeriesRows'
import { getSectorInfo } from '../../helpers/meetstandaard/sectorCatalog'

const COLUMNS = [
    { key: 'name', label: 'Organisatie' },
    { key: 'sectors', label: 'Sector' },
    { key: 'stoppedAt', label: 'Gestopt op' },
    { key: 'note', label: 'Notitie' },
]

/**
 * Een aantal klanten; een klant in twee sectoren telt per sector voor de helft,
 * dus één decimaal.
 *
 * @param {number} value
 * @returns {string}
 */
const formatAantal = (value) => new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(value)

/**
 * @param {string|null} iso
 * @returns {string}
 */
const formatDatum = (iso) => new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric', month: 'short', year: 'numeric',
}).format(new Date(iso))

/**
 * De stopdatum, met wat er over die datum te zeggen valt. 'Geschat' is de
 * datum van het notitiedocument: klanten die vóór het logboek stopten hebben
 * geen eigen stopdatum, en die datum schuift mee met elke notitie.
 *
 * @param {{stoppedAt: string|null, estimated: boolean}} customer
 * @returns {string}
 */
const stopLabel = ({ stoppedAt, estimated }) => {
    if (!stoppedAt) return 'Datum onbekend'
    return estimated ? `${formatDatum(stoppedAt)} (geschat)` : formatDatum(stoppedAt)
}

/**
 * Gestopte klanten (churn) voor de Deccos-beheertenant: per maand en per
 * sector, met daaronder wie het zijn en waarom — de interne notitie uit het
 * abonnementenoverzicht. Bereikbaar vanaf de churntegel.
 *
 * Churn = de stand 'Gestopt' in het abonnementenoverzicht (besluit
 * 2026-10-06), niet een project op inactief.
 *
 * Drie toestanden in deze volgorde: nog niet bekend, mislukt, inhoud.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 */
const PlatformChurn = () => {
    const { churn, loading, error } = usePlatformChurn()

    const { rows, sectors, sectorsSince } = buildSectorSeriesRows(churn ? churn.months : [])

    return (
        <div className='platform-trend'>
            <PageHeader heading='Gestopte klanten' />

            {loading && <p className='platform-trend__state'>Bezig met laden…</p>}

            {!loading && error && (
                <p className='platform-trend__state platform-trend__state--error'>{error}</p>
            )}

            {!loading && !error && churn && churn.total === 0 && (
                <p className='platform-trend__state'>Er zijn geen gestopte klanten.</p>
            )}

            {!loading && !error && churn && churn.total > 0 && (
                <>
                    <div className='dashboard-container platform-trend__card'>
                        <p className='platform-trend__summary'>
                            {`${churn.total} gestopt in totaal, ${churn.thisMonth} deze maand`}
                        </p>
                        <SectorTrendChart
                            rows={rows}
                            sectors={sectors}
                            sectorsSince={sectorsSince}
                            totalKind='bar'
                            totalLabel='Gestopt'
                            formatValue={formatAantal}
                        />
                    </div>

                    {/* dashboard-container en geen table-container: die laatste
                        was tot 2026-10-14 een kijkvenster van 520px, en de
                        gedeelde Table regelt zijn eigen hoogte.
                        AI-CONTEXT: See docs/pitfalls.md#ui-container-class-is-a-scroll-box */}
                    <div className='dashboard-container platform-trend__card'>
                        <Table
                            columns={COLUMNS}
                            items={churn.customers}
                            getItemKey={(c) => c.compagnyId}
                            showSearch={false}
                            emptyMessage='Er zijn geen gestopte klanten.'
                            renderRow={(c) => (
                                <>
                                    <TableCell>{c.name || '—'}</TableCell>
                                    <TableCell>
                                        {c.sectors.length
                                            ? c.sectors.map((slug) => getSectorInfo(slug).label).join(', ')
                                            : '—'}
                                    </TableCell>
                                    <TableCell>{stopLabel(c)}</TableCell>
                                    <TableCell className='platform-trend__note'>{c.note || '—'}</TableCell>
                                </>
                            )}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

export default PlatformChurn
